import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { EL_COLORS } from '@/colors';
import { Flex, FlexCol } from '@/UI/styles';
import { useCaptureElementWheelScroll } from '@/hooks/useCaptureElementWheelScroll';
import { useIsHovered } from '@/hooks/useIsHovered';
import { ThreeDProps } from '../Common/ImageList/types';
import { GridItem } from './GridItem';
import type { NodeId, FileKind } from 'web';
import type { IndexedUploadedAsset } from '../Common/ImageList/ImageList';

const NODE_WIDTH = 422;

type IndextFileKind = FileKind & { globalIndex: number };
type IndexedFile = IndexedUploadedAsset | IndextFileKind;

export interface GridViewerProps {
  nodeId: NodeId;
  files: IndexedFile[];
  selectedIndex: number;
  onFileSelect: (index: number) => void;
  className?: string;
  fillHeight?: boolean;
  shouldUseLegacyFileViewer?: boolean;
  // props for legacy GalleryFileViewer
  threeDProps?: ThreeDProps;
  maxSteps?: number;
  onContextMenu?: (event: React.MouseEvent, index: number) => void;
  onTextChange?: (index: number, newValue: string) => void;
  // eslint-disable-next-line react19-ref/require-ref-object-null
  videoRefs?: React.RefObject<HTMLVideoElement>[];
}

export const GridViewer = ({
  nodeId,
  files,
  selectedIndex,
  onFileSelect,
  className,
  fillHeight = false,
  shouldUseLegacyFileViewer = false,
  // props for legacy GalleryFileViewer
  threeDProps,
  maxSteps,
  onContextMenu,
  onTextChange,
  videoRefs,
}: GridViewerProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isSingleFile = files.length === 1;

  const distributeFilesIntoCloumns = useCallback(() => {
    const fileCount = files.length;
    const columns = fileCount === 1 ? 1 : fileCount <= 4 ? 2 : 3;
    const cols: Array<{ file: IndexedFile }[]> = Array.from({ length: columns }, () => []);

    files.forEach((file, index) => {
      const columnIndex = index % columns;
      cols[columnIndex].push({ file });
    });

    return cols;
  }, [files]);

  const getItemSquareSize = useCallback((columnCount, hasDynamicItemHeight) => {
    const columnWidth = NODE_WIDTH / columnCount;
    return !hasDynamicItemHeight ? columnWidth : undefined;
  }, []);

  const hasDynamicItemHeight = files && (files[0]?.type === 'image' || files[0]?.type === 'video');
  const isTextItem = files && files[0]?.type === 'text';
  const fillItemHeight = isSingleFile && (fillHeight || isTextItem);
  const gridColumns = useMemo(distributeFilesIntoCloumns, [distributeFilesIntoCloumns]);

  const fixedItemSquareSize = useMemo(
    () => (fillItemHeight ? undefined : getItemSquareSize(gridColumns.length, hasDynamicItemHeight)),
    [gridColumns.length, hasDynamicItemHeight, getItemSquareSize, fillItemHeight],
  );

  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  useCaptureElementWheelScroll(isHovered ? scrollContainerRef : { current: null });

  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || fillItemHeight) {
      setHasOverflow(false);
      return;
    }
    const setOverflow = () => setHasOverflow(el.scrollHeight > el.clientHeight);

    setOverflow();
    const observer = new ResizeObserver(setOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [fillItemHeight]);

  return (
    <FlexCol className={className} sx={{ width: '100%', flex: 1, minHeight: 0 }}>
      <Box
        ref={scrollContainerRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          flex: 1,
          minHeight: 0,
          ...(!fillHeight && !isSingleFile && { maxHeight: 436 }),
          ...(fillItemHeight && { display: 'flex', flexDirection: 'column' }),
          overflow: fillItemHeight ? 'hidden' : 'auto',
          border: `1px solid ${EL_COLORS.BoxBorder}`,
          p: 0.25,
          pr: hasOverflow ? 2 : 0.25,
          borderRadius: 2,
        }}
      >
        <Flex sx={{ gap: 0.5, ...(fillItemHeight && { flex: 1, minHeight: 0 }) }}>
          {gridColumns.map((columnFiles, columnIndex) => (
            <FlexCol key={`column-${columnIndex}`} sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
              {columnFiles.map(({ file }) => (
                <GridItem
                  shouldUseLegacyFileViewer={shouldUseLegacyFileViewer}
                  key={`file-${file.globalIndex}`}
                  nodeId={nodeId}
                  file={file}
                  index={file.globalIndex}
                  isSelected={file.globalIndex === selectedIndex}
                  onFileClick={onFileSelect}
                  selected={selectedIndex}
                  fillHeight={fillItemHeight}
                  // props for legacy GalleryFileViewer
                  threeDProps={threeDProps}
                  maxSteps={maxSteps}
                  onContextMenu={onContextMenu}
                  onTextChange={onTextChange}
                  videoRef={videoRefs?.[file.globalIndex]}
                  fixedItemSize={fixedItemSquareSize}
                />
              ))}
            </FlexCol>
          ))}
        </Flex>
      </Box>
    </FlexCol>
  );
};
